// src/components/admin/users/UserManagement.jsx
import { useState } from 'react';
import { FaUsers, FaUserPlus, FaSearch, FaTrash, FaEye } from 'react-icons/fa';
import useCustomers from '../../../hooks/useCustomers';
import AddCustomerModal from './AddCustomerModal';
import CustomerDetails from './CustomerDetails';

const UserManagement = () => {
  const { customers, loading, error, clearError, addCustomer, updateCustomer, deleteCustomer } =
    useCustomers();

  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const filtered = customers.filter((c) => {
    const term = search.toLowerCase();
    return (
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(term) ||
      c.email.toLowerCase().includes(term) ||
      (c.phone || '').includes(term) ||
      (c.address || '').toLowerCase().includes(term)
    );
  });

  const handleAddCustomer = async (data) => {
    setIsAdding(true);
    try {
      await addCustomer(data);
      setShowAddModal(false);
      showSuccess(`${data.firstName} ${data.lastName} added successfully`);
    } finally {
      setIsAdding(false);
    }
  };

  const handleView = (customer) => {
    setSelectedCustomer(customer);
    setShowDetails(true);
  };

  const handleUpdate = async (id, data) => {
    await updateCustomer(id, data);
    // Update the selected customer so the modal reflects changes immediately
    setSelectedCustomer((prev) => ({ ...prev, ...data }));
    showSuccess('Customer updated successfully');
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Delete ${customer.firstName} ${customer.lastName}? This cannot be undone.`)) return;
    try {
      await deleteCustomer(customer.id);
      showSuccess(`${customer.firstName} ${customer.lastName} deleted`);
    } catch (err) {
      alert(`Failed to delete: ${err.message}`);
    }
  };

  const fmtDate = (date) => {
    if (!date) return '—';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    }).format(date);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? '...' : `${customers.length} customer${customers.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
        >
          <FaUserPlus className="w-4 h-4" />
          Add Customer
        </button>
      </div>

      {/* Success */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-3 text-sm">
          {successMessage}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center justify-between bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 text-sm">
          {error}
          <button onClick={clearError} className="ml-4 text-red-500 hover:text-red-700 text-xs underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, phone..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center space-y-2">
              <div className="w-8 h-8 border-2 border-gray-200 border-t-primary-500 rounded-full animate-spin mx-auto" />
              <p className="text-sm text-gray-400">Loading customers...</p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <FaUsers className="w-10 h-10 mb-3 text-gray-300" />
            <p className="text-sm font-medium text-gray-500">
              {search ? 'No customers match your search' : 'No customers yet'}
            </p>
            {!search && (
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Add your first customer
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    {['Name', 'Email', 'Phone', 'Address', 'Added', ''].map((h, i) => (
                      <th
                        key={i}
                        className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((customer) => (
                    <tr key={customer.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-semibold text-primary-700">
                              {customer.firstName[0]}{customer.lastName[0]}
                            </span>
                          </div>
                          <span className="font-medium text-gray-900">
                            {customer.firstName} {customer.lastName}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600">{customer.email}</td>
                      <td className="px-6 py-4 text-gray-500">{customer.phone || '—'}</td>
                      <td className="px-6 py-4 text-gray-500 max-w-[200px] truncate">
                        {customer.address || '—'}
                      </td>
                      <td className="px-6 py-4 text-gray-400">{fmtDate(customer.createdAt)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleView(customer)}
                            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            title="View profile"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(customer)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete customer"
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filtered.map((customer) => (
                <div key={customer.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-primary-700">
                        {customer.firstName[0]}{customer.lastName[0]}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {customer.firstName} {customer.lastName}
                      </p>
                      <p className="text-sm text-gray-500">{customer.email}</p>
                      {customer.phone && (
                        <p className="text-xs text-gray-400">{customer.phone}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleView(customer)}
                      className="p-2 text-gray-400 hover:text-primary-600 rounded-lg"
                    >
                      <FaEye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(customer)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <AddCustomerModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddCustomer}
        isLoading={isAdding}
      />

      <CustomerDetails
        customer={selectedCustomer}
        isOpen={showDetails}
        onClose={() => { setShowDetails(false); setSelectedCustomer(null); }}
        onUpdate={handleUpdate}
      />
    </div>
  );
};

export default UserManagement;